/**
 * Validation + value types for the encrypted Web Push payload that the
 * Service Worker receives via the `push` event.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/NSEPushPayload.swift` exactly.
 *
 * Threat model:
 *   - Malformed / partial payloads: all required fields are pulled by exact
 *     key from the `kaether` sub-object, validated non-empty, and
 *     length-bounded so a giant envelope cannot blow the SW memory budget.
 *   - Server-supplied display strings: the parser exposes only the ciphertext
 *     envelope, the sender userId, and the messageId — never a peer- or
 *     server-claimed `title` / `body` / `from_name`. The render layer
 *     (`SWRenderPolicy`) gets sender / preview exclusively from the LOCAL
 *     roster.
 *   - Channel confusion: parsing pulls strictly from `payload["kaether"]`;
 *     anything outside that sub-object is ignored.
 *
 * Pure-logic only: no `self.registration.showNotification()` calls.
 */

// ---------------------------------------------------------------------------
// Value types
// ---------------------------------------------------------------------------

/**
 * Validated kaether sub-object extracted from the Web Push JSON payload.
 * All fields have been bounds-checked and non-empty-validated by the parser.
 */
export interface SWPushKaetherPayload {
  /** Base64url-encoded ciphertext envelope, ≤ MAX_ENVELOPE_CHARS chars. */
  readonly envelope: string;
  /** Opaque sender user identifier from the `from` key. */
  readonly fromUserId: string;
  /** Opaque ULID message identifier from the `msgId` key. */
  readonly messageId: string;
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type SWPushPayloadErrorKind =
  | { readonly tag: "missingKaetherKey" }
  | { readonly tag: "missingEnvelope" }
  | { readonly tag: "missingFromUserId" }
  | { readonly tag: "missingMessageId" }
  | { readonly tag: "emptyEnvelope" }
  | { readonly tag: "oversizeEnvelope"; readonly length: number }
  | { readonly tag: "invalidPayloadJson" };

export class SWPushPayloadError extends Error {
  constructor(public readonly kind: SWPushPayloadErrorKind) {
    super(`SWPushPayloadError: ${kind.tag}`);
    this.name = "SWPushPayloadError";
  }
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Parser for the Web Push JSON payload's `kaether` sub-object.
 *
 * Mirrors `NSEPushPayloadParser` from the iOS Notification Service Extension.
 */
export const SWPushPayloadParser = {
  /**
   * APNs payload cap rules out anything bigger anyway; we enforce locally
   * to bound SW memory before we spend cycles base64-decoding.
   *
   * Mirrors `NSEPushPayloadParser.maxEnvelopeChars = 8000`.
   */
  MAX_ENVELOPE_CHARS: 8000 as const,

  /**
   * Parse a raw Web Push JSON payload object into a validated
   * `SWPushKaetherPayload`. The parser only reads the `"kaether"`
   * sub-object — any other keys (including `"aps"`, `"from_name"`, or
   * any server-supplied display string) are ignored.
   *
   * @throws {SWPushPayloadError} on any structural or size violation.
   */
  parse(raw: unknown): SWPushKaetherPayload {
    // Accept either a pre-parsed object or a JSON string (SW push data
    // arrives as a string via `event.data.text()`).
    let obj: Record<string, unknown>;
    if (typeof raw === "string") {
      try {
        obj = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        throw new SWPushPayloadError({ tag: "invalidPayloadJson" });
      }
    } else if (raw !== null && typeof raw === "object") {
      obj = raw as Record<string, unknown>;
    } else {
      throw new SWPushPayloadError({ tag: "missingKaetherKey" });
    }

    // Pull the kaether sub-object — ignore everything else.
    const sub = obj["kaether"];
    if (sub === undefined || sub === null || typeof sub !== "object") {
      throw new SWPushPayloadError({ tag: "missingKaetherKey" });
    }
    const kaether = sub as Record<string, unknown>;

    // envelope
    const envelope = kaether["envelope"];
    if (envelope === undefined || envelope === null) {
      throw new SWPushPayloadError({ tag: "missingEnvelope" });
    }
    if (typeof envelope !== "string") {
      throw new SWPushPayloadError({ tag: "missingEnvelope" });
    }
    if (envelope.length === 0) {
      throw new SWPushPayloadError({ tag: "emptyEnvelope" });
    }
    if (envelope.length > SWPushPayloadParser.MAX_ENVELOPE_CHARS) {
      throw new SWPushPayloadError({
        tag: "oversizeEnvelope",
        length: envelope.length,
      });
    }

    // fromUserId  (wire key: "from", mirrors iOS)
    const from = kaether["from"];
    if (from === undefined || from === null || typeof from !== "string" || from.length === 0) {
      throw new SWPushPayloadError({ tag: "missingFromUserId" });
    }

    // messageId  (wire key: "msgId", mirrors iOS)
    const msgId = kaether["msgId"];
    if (msgId === undefined || msgId === null || typeof msgId !== "string" || msgId.length === 0) {
      throw new SWPushPayloadError({ tag: "missingMessageId" });
    }

    return {
      envelope,
      fromUserId: from,
      messageId: msgId,
    };
  },
} as const;
